import { motion } from 'framer-motion'
import { 
  Settings as SettingsIcon, 
  User, 
  Mail, 
  Phone, 
  MapPin,
  Save,
  Globe,
  Shield,
  Bell,
  Eye,
  Palette,
  Volume2,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Youtube
} from 'lucide-react'
import { useStore } from '../../store/useStore'
import { useState } from 'react'
import GlassCard from '../../components/ui/GlassCard'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import Textarea from '../../components/ui/Textarea'
import AdminLayout from '../../components/admin/AdminLayout'

const Settings = () => {
  const { 
    user, 
    preferences, 
    setPreferences, 
    sceneSettings, 
    setSceneSettings,
    socialMediaLinks,
    updateSocialMediaLinks
  } = useStore()
  
  const [activeTab, setActiveTab] = useState('general')
  const [formData, setFormData] = useState({
    email: 's.massena@videotron.ca',
    phone: '',
    address: '',
    website: '',
    facebook: '',
    instagram: '',
    twitter: '',
    linkedin: '',
    youtube: ''
  })

  const tabs = [
    { id: 'general', label: 'Général', icon: SettingsIcon },
    { id: 'social', label: 'Réseaux Sociaux', icon: Globe },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'appearance', label: 'Apparence', icon: Palette }
  ]

  const handleSave = () => {
    // Sauvegarder les paramètres
    console.log('Sauvegarde des paramètres:', formData)
    // Ici on pourrait envoyer les données au serveur
  }

  const handleSocialSave = () => {
    const links = []
    
    Object.entries(formData).forEach(([platform, url]) => {
      if (['facebook', 'instagram', 'twitter', 'linkedin', 'youtube'].includes(platform) && url.trim()) {
        links.push({
          platform,
          url: url.trim(),
          isActive: true
        })
      }
    })
    
    updateSocialMediaLinks(links)
    console.log('Réseaux sociaux sauvegardés:', links)
  }

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto"
        >
          {/* En-tête */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
              <SettingsIcon size={32} className="mr-3 text-blue-600" />
              Paramètres
            </h1>
            <p className="text-gray-600">
              Gérez les paramètres généraux de votre application et configurez vos réseaux sociaux.
            </p>
          </div>

          {/* Onglets */}
          <div className="mb-8">
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
                      activeTab === tab.id
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Icon size={16} />
                    <span>{tab.label}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Contenu des onglets */}
          <div className="space-y-6">
            {/* Onglet Général */}
            {activeTab === 'general' && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <GlassCard className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <User size={20} className="mr-2" />
                    Informations Générales
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Principal
                      </label>
                      <Input
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="admin@residencemassena.com"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Tous les emails seront redirigés vers cette adresse
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Téléphone
                      </label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="(450) 123-4567"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Adresse
                      </label>
                      <Textarea
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="123 Rue Masséna, Laval, QC H7N 1A1"
                        rows={3}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Site Web
                      </label>
                      <Input
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        placeholder="https://www.residencemassena.com"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-6 flex justify-end">
                    <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">
                      <Save size={16} className="mr-2" />
                      Sauvegarder
                    </Button>
                  </div>
                </GlassCard>
              </motion.div>
            )}

            {/* Onglet Réseaux Sociaux */}
            {activeTab === 'social' && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <GlassCard className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <Globe size={20} className="mr-2" />
                    Réseaux Sociaux
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-3">
                        <Facebook size={20} className="text-blue-600" />
                        <Input
                          value={formData.facebook}
                          onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                          placeholder="https://facebook.com/residencemassena"
                        />
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <Instagram size={20} className="text-pink-600" />
                        <Input
                          value={formData.instagram}
                          onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                          placeholder="https://instagram.com/residencemassena"
                        />
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <Twitter size={20} className="text-blue-400" />
                        <Input
                          value={formData.twitter}
                          onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                          placeholder="https://twitter.com/residencemassena"
                        />
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <Linkedin size={20} className="text-blue-700" />
                        <Input
                          value={formData.linkedin}
                          onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                          placeholder="https://linkedin.com/company/residencemassena"
                        />
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <Youtube size={20} className="text-red-600" />
                        <Input
                          value={formData.youtube}
                          onChange={(e) => setFormData({ ...formData, youtube: e.target.value })}
                          placeholder="https://youtube.com/residencemassena"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 flex justify-end">
                    <Button onClick={handleSocialSave} className="bg-green-600 hover:bg-green-700">
                      <Save size={16} className="mr-2" />
                      Sauvegarder Réseaux Sociaux
                    </Button>
                  </div>
                </GlassCard>
              </motion.div>
            )}

            {/* Onglet Notifications */}
            {activeTab === 'notifications' && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <GlassCard className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <Bell size={20} className="mr-2" />
                    Notifications Email
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h3 className="font-semibold text-blue-900 mb-2">Redirection Email</h3>
                      <p className="text-blue-800 text-sm mb-3">
                        Tous les emails envoyés à <strong>admin@residencemassena.com</strong> seront automatiquement redirigés vers <strong>s.massena@videotron.ca</strong>
                      </p>
                      <div className="flex items-center space-x-2 text-sm text-blue-700">
                        <Mail size={16} />
                        <span>admin@residencemassena.com → s.massena@videotron.ca</span>
                      </div>
                    </div>
                    
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h3 className="font-semibold text-green-900 mb-2">Notifications Actives</h3>
                      <ul className="text-green-800 text-sm space-y-1">
                        <li>• Nouvelles candidatures d'emploi</li>
                        <li>• Messages de contact</li>
                        <li>• Demandes de nouveaux administrateurs</li>
                        <li>• Erreurs système</li>
                      </ul>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            )}

            {/* Onglet Apparence */}
            {activeTab === 'appearance' && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <GlassCard className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <Palette size={20} className="mr-2" />
                    Apparence
                  </h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Couleur Principale
                      </label>
                      <div className="flex space-x-2">
                        {['#0ea5e9', '#d946ef', '#10b981', '#f59e0b'].map((color) => (
                          <button
                            key={color}
                            className="w-8 h-8 rounded-full border-2 border-gray-300"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="animations"
                        checked={preferences.showAnimations}
                        onChange={(e) => setPreferences({ ...preferences, showAnimations: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="animations" className="text-sm text-gray-700">
                        Activer les animations
                      </label>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </AdminLayout>
  )
}

export default Settings